import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ShippingMeanService } from '../../../_services/shipping-mean.service';
import { CountryService } from '../../../_services/country.service';
import { ProductsConditionsService } from '../../../_services/products-conditions.service';
import { User } from '../../../_models/user';

@Component({
    selector: 'app-shipping-option-detail',
    templateUrl: './shipping-option-detail.component.html',
    styleUrls: ['./shipping-option-detail.component.css'],
    standalone: false
})
export class ShippingOptionDetailComponent implements OnInit {
    optionDetail: any = null;
    user: User;
    loading = true;
    error = false;
    optionId: number = 0;

    // Shipping type detection
    isInterCity = false;
    isIntraCity = false;

    // Pricing form state
    showPricingForm = false;
    pricingForm: FormGroup;
    submittingPricing = false;
    countries: any[] = [];
    cities: any[] = [];
    productConditions: any[] = [];
    allCountries = true;
    allCities = true;
    selectedCountryIds: any[] = [];
    selectedCityIds: any[] = [];

    // Modal state
    showCountryModal = false;
    showCityModal = false;
    modalCountryIds: any[] = [];
    modalCityIds: any[] = [];

    // Pricing list state
    shippingPricings: any[] = [];
    loadingPricings = false;
    selectedPricing: any = null;
    showPricingDetail = false;

    // Pricing edit/delete state
    selectedPricingForEdit: any = null;
    isEditingPricing = false;
    pricingToDelete: any = null;
    showDeletePricingModal = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private shippingMeanService: ShippingMeanService,
        private countryService: CountryService,
        private productsConditionsService: ProductsConditionsService,
        private toastr: ToastrService,
        private translateService: TranslateService
    ) {
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.pricingForm = this.fb.group({
            price: ['', [Validators.required, Validators.min(0)]],
            packagingDetails: ['', Validators.required],
            eta: ['', [Validators.required, Validators.min(1)]],
            productConditionId: ['', Validators.required],
            weightMin: [''],
            weightMax: [''],
            distanceMin: [''],
            distanceMax: ['']
        });
    }

    ngOnInit(): void {
        this.optionId = Number(this.route.snapshot.paramMap.get('id'));
        if (this.optionId) {
            this.loadOptionDetail();
            this.loadCountries();
            this.loadProductConditions();
        } else {
            this.error = true;
            this.loading = false;
        }
    }

    loadOptionDetail(): void {
        this.loading = true;
        this.shippingMeanService.getShippingOption(this.optionId).subscribe({
            next: (response: any) => {
                this.optionDetail = response?.data || null;
                this.loading = false;
                if (!this.optionDetail) {
                    this.error = true;
                } else {
                    // Detect shipping type
                    const shippingTypeCode = this.optionDetail.shippingType?.code || '';
                    this.isInterCity = shippingTypeCode === 'INTERCITY';
                    this.isIntraCity = shippingTypeCode === 'INTRACITY';
                    
                    if ((this.isInterCity || this.isIntraCity) && this.optionDetail.country?.id) {
                        this.loadCities(this.optionDetail.country.id);
                    }
                    this.loadShippingPricings();
                }
            },
            error: () => {
                this.loading = false;
                this.error = true;
                this.translateService.get('shipping_options.load_error').subscribe(message => {
                    this.toastr.error(message);
                });
            }
        });
    }

    loadCountries(): void {
        this.countryService.getCountries().subscribe({
            next: (countries) => {
                this.countries = countries;
            },
            error: () => {
                this.toastr.error('Failed to load countries');
            }
        });
    }

    loadCities(countryId: number): void {
        this.shippingMeanService.getCitiesByCountry(countryId).subscribe({
            next: (response: any) => {
                this.cities = response?.data || response || [];
            },
            error: () => {
                this.toastr.error('Failed to load cities');
            }
        });
    }

    loadProductConditions(): void {
        this.productsConditionsService.get_product_conditions().subscribe({
            next: (conditions) => {
                this.productConditions = conditions;
            },
            error: () => {
                this.toastr.error('Failed to load product conditions');
            }
        });
    }

    loadShippingPricings(): void {
        this.loadingPricings = true;
        this.shippingMeanService.getShippingPricings(this.optionId).subscribe({
            next: (response: any) => {
                this.shippingPricings = response?.data?.data || [];
                this.loadingPricings = false;
            },
            error: () => {
                this.shippingPricings = [];
                this.loadingPricings = false;
            }
        });
    }

    viewPricingDetail(pricing: any): void {
        this.shippingMeanService.getShippingPricing(pricing.id).subscribe({
            next: (response: any) => {
                this.selectedPricing = response?.data || null;
                this.showPricingDetail = true;
            },
            error: () => {
                this.translateService.get('shipping_pricing.load_error').subscribe(message => {
                    this.toastr.error(message);
                });
            }
        });
    }

    closePricingDetail(): void {
        this.selectedPricing = null;
        this.showPricingDetail = false;
    }

    editPricing(pricing: any): void {
        this.shippingMeanService.getShippingPricing(pricing.id).subscribe({
            next: (response: any) => {
                const pricingDetail = response?.data || null;
                if (pricingDetail) {
                    this.selectedPricingForEdit = pricingDetail;
                    this.isEditingPricing = true;
                    this.showPricingForm = true;
                    
                    // Populate form
                    this.pricingForm.patchValue({
                        price: pricingDetail.price,
                        packagingDetails: pricingDetail.packagingDetails,
                        eta: pricingDetail.eta,
                        productConditionId: pricingDetail.productCondition?.id || pricingDetail.productConditionId,
                        weightMin: pricingDetail.weightMin,
                        weightMax: pricingDetail.weightMax,
                        distanceMin: pricingDetail.distanceMin,
                        distanceMax: pricingDetail.distanceMax
                    });
                    
                    // Set flags and pre-selected IDs
                    this.selectedCountryIds = pricingDetail.countries?.map(c => c.id) || [];
                    this.selectedCityIds = pricingDetail.cities?.map(c => c.id) || [];
                    
                    // Force checkbox sections to be visible if there are pre-selected items
                    this.allCountries = pricingDetail.allCountries;
                    this.allCities = pricingDetail.allCities;
                    
                    if (this.selectedCountryIds.length > 0) {
                        this.allCountries = false;
                    }
                    if (this.selectedCityIds.length > 0) {
                        this.allCities = false;
                    }
                }
            },
            error: () => {
                this.translateService.get('shipping_pricing.load_error').subscribe(message => {
                    this.toastr.error(message);
                });
            }
        });
    }

    cancelPricingEdit(): void {
        this.resetPricingForm();
        this.showPricingForm = false;
    }

    confirmDeletePricing(pricing: any): void {
        this.pricingToDelete = pricing;
        this.showDeletePricingModal = true;
    }

    closeDeletePricingModal(): void {
        this.pricingToDelete = null;
        this.showDeletePricingModal = false;
    }

    deletePricing(): void {
        if (!this.pricingToDelete) {
            return;
        }

        this.shippingMeanService.deleteShippingPricing(this.pricingToDelete.id).subscribe({
            next: () => {
                this.translateService.get('shipping_pricing.delete_success').subscribe(message => {
                    this.toastr.success(message);
                });
                this.closeDeletePricingModal();
                this.loadShippingPricings();
            },
            error: () => {
                this.translateService.get('shipping_pricing.delete_error').subscribe(message => {
                    this.toastr.error(message);
                });
                this.closeDeletePricingModal();
            }
        });
    }

    togglePricingForm(): void {
        this.showPricingForm = !this.showPricingForm;
        if (!this.showPricingForm) {
            this.resetPricingForm();
        }
    }

    resetPricingForm(): void {
        this.pricingForm.reset();
        this.allCountries = true;
        this.allCities = true;
        this.selectedCountryIds = [];
        this.selectedCityIds = [];
        this.submittingPricing = false;
        this.isEditingPricing = false;
        this.selectedPricingForEdit = null;
    }

    toggleAllCountries(): void {
        this.allCountries = !this.allCountries;
        if (!this.allCountries) {
            this.modalCountryIds = [...this.selectedCountryIds];
            this.showCountryModal = true;
        } else {
            this.selectedCountryIds = [];
        }
    }

    toggleAllCities(): void {
        this.allCities = !this.allCities;
        if (!this.allCities) {
            this.modalCityIds = [...this.selectedCityIds];
            this.showCityModal = true;
        } else {
            this.selectedCityIds = [];
        }
    }

    openCountryModal(): void {
        this.modalCountryIds = [...this.selectedCountryIds];
        this.showCountryModal = true;
    }

    confirmCountrySelection(): void {
        this.selectedCountryIds = [...this.modalCountryIds];
        this.showCountryModal = false;
        if (this.selectedCountryIds.length === 0) {
            this.allCountries = true;
        }
    }

    cancelCountrySelection(): void {
        this.showCountryModal = false;
        if (this.selectedCountryIds.length === 0) {
            this.allCountries = true;
        }
    }

    removeCountry(countryId: any): void {
        this.selectedCountryIds = this.selectedCountryIds.filter(id => id !== countryId);
        if (this.selectedCountryIds.length === 0) {
            this.allCountries = true;
        }
    }

    getSelectedCountries(): any[] {
        return this.countries.filter(c => this.selectedCountryIds.includes(c.id));
    }

    getModalCountries(): any[] {
        return this.countries.filter(c => this.modalCountryIds.includes(c.id));
    }

    removeCountryFromModal(countryId: any): void {
        this.modalCountryIds = this.modalCountryIds.filter(id => id !== countryId);
    }

    openCityModal(): void {
        this.modalCityIds = [...this.selectedCityIds];
        this.showCityModal = true;
    }

    confirmCitySelection(): void {
        this.selectedCityIds = [...this.modalCityIds];
        this.showCityModal = false;
        if (this.selectedCityIds.length === 0) {
            this.allCities = true;
        }
    }

    cancelCitySelection(): void {
        this.showCityModal = false;
        if (this.selectedCityIds.length === 0) {
            this.allCities = true;
        }
    }

    removeCity(cityId: any): void {
        this.selectedCityIds = this.selectedCityIds.filter(id => id !== cityId);
        if (this.selectedCityIds.length === 0) {
            this.allCities = true;
        }
    }

    getSelectedCities(): any[] {
        return this.cities.filter(c => this.selectedCityIds.includes(c.id));
    }

    getModalCities(): any[] {
        return this.cities.filter(c => this.modalCityIds.includes(c.id));
    }

    removeCityFromModal(cityId: any): void {
        this.modalCityIds = this.modalCityIds.filter(id => id !== cityId);
    }

    onPricingSubmit(): void {
        if (this.pricingForm.invalid) {
            return;
        }

        this.submittingPricing = true;
        const formValue = this.pricingForm.value;

        let payload: any;

        if (this.isInterCity || this.isIntraCity) {
            payload = {
                price: formValue.price,
                packagingDetails: formValue.packagingDetails,
                eta: formValue.eta,
                shippingOptionId: this.optionId,
                productConditionId: formValue.productConditionId,
                allCountries: false,
                allCities: this.allCities,
                countryIds: [],
                cityIds: this.allCities ? [] : this.selectedCityIds,
                weightMin: formValue.weightMin || 0,
                weightMax: formValue.weightMax || 0,
                distanceMin: this.isIntraCity ? (formValue.distanceMin || 0) : 0,
                distanceMax: this.isIntraCity ? (formValue.distanceMax || 0) : 0
            };
        } else {
            payload = {
                price: formValue.price,
                packagingDetails: formValue.packagingDetails,
                eta: formValue.eta,
                shippingOptionId: this.optionId,
                productConditionId: formValue.productConditionId,
                allCountries: this.allCountries,
                allCities: false,
                countryIds: this.allCountries ? [] : this.selectedCountryIds,
                cityIds: [],
                weightMin: 0,
                weightMax: 0,
                distanceMin: 0,
                distanceMax: 0
            };
        }

        if (this.isEditingPricing && this.selectedPricingForEdit) {
            this.shippingMeanService.updateShippingPricing(this.selectedPricingForEdit.id, payload).subscribe({
                next: () => {
                    this.translateService.get('shipping_pricing.update_success').subscribe(message => {
                        this.toastr.success(message);
                    });
                    this.resetPricingForm();
                    this.showPricingForm = false;
                    this.loadShippingPricings();
                },
                error: () => {
                    this.translateService.get('shipping_pricing.update_error').subscribe(message => {
                        this.toastr.error(message);
                    });
                    this.submittingPricing = false;
                }
            });
        } else {
            this.shippingMeanService.addShippingPricing(payload).subscribe({
                next: () => {
                    this.translateService.get('shipping_pricing.success').subscribe(message => {
                        this.toastr.success(message);
                    });
                    this.resetPricingForm();
                    this.showPricingForm = false;
                    this.loadShippingPricings();
                },
                error: () => {
                    this.translateService.get('shipping_pricing.error').subscribe(message => {
                        this.toastr.error(message);
                    });
                    this.submittingPricing = false;
                }
            });
        }
    }

    goBack(): void {
        this.router.navigate(['/user/shipping-means']);
    }
}
